/**
 * School admin → Settings.
 *
 * Signup policy and the two teacher-delegation switches. Both switches default
 * off, and the copy says what turning them on actually permits — a school that
 * has not thought about it should get the safe answer, and one that opts in
 * should know it is delegating credential authority.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPatch } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { SIGNUP_POLICIES } from '@cwy/config/contracts';
import type { SignupPolicy } from '@cwy/config/contracts';
import { Button, Card, Field, Select, StudentPage, StudentPageHeader } from '@/components/ds';

interface Settings {
  id: string;
  name: string;
  slug: string;
  signupPolicy: SignupPolicy;
  teachersMayCreateStudents: boolean;
  teachersMayResetStudents: boolean;
  studentsMayEditOwnName: boolean;
}

const POLICY_LABEL: Record<SignupPolicy, string> = {
  CLOSED: 'Sign-in only — staff create every account',
  ROSTER_ACTIVATION: 'Students activate a roster record with a code',
  INVITE_CODE: 'Students register with an invitation',
  OPEN_APPROVAL: 'Students request access, staff approve',
};

function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 3, width: 16, height: 16, accentColor: 'var(--brand-forest)' }}
      />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-ink">{label}</span>
        <span className="mt-0.5 block text-xs text-ink-soft">{hint}</span>
      </span>
    </label>
  );
}

export function SchoolSettingsPage() {
  const settings = useQuery({
    queryKey: ['tenant-admin', 'settings'],
    queryFn: () => apiGet<Settings>('/tenant-admin/settings'),
  });

  const [draft, setDraft] = useState<Settings | null>(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // Hydrate once. Guarded on `draft` so a background refetch cannot discard
  // edits someone is part-way through.
  useEffect(() => {
    if (!draft && settings.data) setDraft(settings.data);
  }, [settings.data, draft]);

  const save = useMutation({
    mutationFn: (next: Settings) =>
      apiPatch('/tenant-admin/settings', {
        signupPolicy: next.signupPolicy,
        teachersMayCreateStudents: next.teachersMayCreateStudents,
        teachersMayResetStudents: next.teachersMayResetStudents,
        studentsMayEditOwnName: next.studentsMayEditOwnName,
      }),
    onSuccess: () => {
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
      void queryClient.invalidateQueries({ queryKey: ['tenant-admin', 'settings'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (!draft) return <p className="text-ink-soft">Loading settings…</p>;

  return (
    <StudentPage>
      <StudentPageHeader
        title="School settings"
        subtitle={`${draft.name} · students sign in at /school/${draft.slug}`}
      />

      {error && <p className="text-sm text-danger">{error}</p>}

      <Card title="How students get accounts">
        {/* Shown, not offered. Only “sign-in only” is built end to end — the
            other three name flows (activation codes, invitations, approval
            queues) that have no screens behind them yet, and a school that
            picked one would get a policy that silently did nothing. */}
        <Field
          label="Sign-up policy"
          hint="Every account is created by your staff. The other policies are not available yet."
        >
          {({ id }) => (
            <Select
              id={id}
              disabled
              value={draft.signupPolicy}
              onChange={(e) => setDraft({ ...draft, signupPolicy: e.target.value as SignupPolicy })}
            >
              {SIGNUP_POLICIES.map((value) => (
                <option key={value} value={value}>
                  {POLICY_LABEL[value]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </Card>

      <Card title="What teachers can do">
        <div className="flex flex-col gap-4">
          <Toggle
            label="Teachers can add students"
            hint="Only to their own classes. Off by default — with it off, admins create every student."
            checked={draft.teachersMayCreateStudents}
            onChange={(next) => setDraft({ ...draft, teachersMayCreateStudents: next })}
          />
          <Toggle
            label="Teachers can reset student passwords"
            hint="Only for students in their own classes. This lets a teacher take over a student account, so turn it on only if your teachers handle their own class admin."
            checked={draft.teachersMayResetStudents}
            onChange={(next) => setDraft({ ...draft, teachersMayResetStudents: next })}
          />
        </div>
      </Card>

      <Card title="What students can do">
        <Toggle
          label="Students can change their own name"
          hint="Off by default, so your roster keeps the names your school recorded. Students can still ask you to correct theirs."
          checked={draft.studentsMayEditOwnName}
          onChange={(next) => setDraft({ ...draft, studentsMayEditOwnName: next })}
        />
      </Card>

      <div className="flex items-center gap-3">
        <Button
          disabled={save.isPending}
          onClick={() => {
            setError('');
            save.mutate(draft);
          }}
        >
          {save.isPending ? 'Saving…' : 'Save settings'}
        </Button>
        {saved && <span className="text-sm text-ink-soft">Saved.</span>}
      </div>
    </StudentPage>
  );
}
